import React, { useState } from 'react'
import { Card, Button, IconButton } from '../../ui'
import { InlineManagerNote } from '../annotations/InlineManagerNote.jsx'

/**
 * Card 1 for Side-quests track.
 * Free-form list of items, each with a title and optional detail.
 */
export function SideQuestListCard({
  tag = 'FACT',
  title = 'What did you work on that wasn\'t planned?',
  items = [],
  onChange,
  placeholder = 'Add the unplanned work that mattered this period.',
  readOnly = false,
  isClosed = false,
  managerNote = '',
  onManagerNoteChange,
  onSuggestManagerNote,
  isManager = false,
  isReady = false,
  isShared = false,
}) {
  const [newTitle, setNewTitle] = useState('')

  function handleAdd() {
    if (!newTitle.trim()) return
    const newItem = {
      id: crypto.randomUUID(),
      title: newTitle.trim(),
      detail: '',
    }
    onChange?.([...items, newItem])
    setNewTitle('')
  }

  function handleRemove(id) {
    onChange?.(items.filter((item) => item.id !== id))
  }

  function handleUpdate(id, field, value) {
    onChange?.(items.map((item) => (item.id === id ? { ...item, [field]: value } : item)))
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  return (
    <Card style={isClosed ? { background: 'var(--locked-bg)' } : undefined}>
      <div style={styles.container}>
        <span style={styles.tag}>{tag}</span>
        <h4 style={styles.title}>{title}</h4>

        {items.length === 0 && readOnly && (
          <span style={styles.muted}>None added</span>
        )}

        <div style={styles.list}>
          {items.map((item) => (
            <div key={item.id} style={styles.item}>
              <div style={styles.itemHeader}>
                {readOnly ? (
                  <span style={styles.itemTitle}>{item.title}</span>
                ) : (
                  <input
                    style={styles.itemTitleInput}
                    value={item.title}
                    onChange={(e) => handleUpdate(item.id, 'title', e.target.value)}
                    disabled={readOnly}
                    aria-label="Item title"
                  />
                )}
                {!readOnly && (
                  <IconButton
                    icon="x"
                    size="sm"
                    onClick={() => handleRemove(item.id)}
                    aria-label="Remove item"
                  />
                )}
              </div>
              {readOnly ? (
                item.detail && <p style={styles.detailText}>{item.detail}</p>
              ) : (
                <textarea
                  style={{ ...styles.textarea, background: 'transparent' }}
                  value={item.detail || ''}
                  onChange={(e) => handleUpdate(item.id, 'detail', e.target.value)}
                  placeholder="Add detail..."
                  disabled={readOnly}
                  rows={1}
                  aria-label="Item detail"
                />
              )}
            </div>
          ))}
        </div>

        {!readOnly && (
          <div style={styles.addRow}>
            <input
              style={styles.addInput}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              aria-label="New item title"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAdd}
              disabled={!newTitle.trim()}
            >
              Add
            </Button>
          </div>
        )}
        <InlineManagerNote
          value={managerNote}
          onChange={onManagerNoteChange}
          onSuggest={onSuggestManagerNote}
          isManager={isManager}
          isReady={isReady}
          isShared={isShared}
          isClosed={isClosed}
        />
      </div>
    </Card>
  )
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: '8px' },
  tag: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-secondary)',
  },
  title: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
  },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '8px',
    border: '1px solid var(--border)',
    borderRadius: '8px',
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
  },
  itemTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    flex: 1,
  },
  itemTitleInput: {
    flex: 1,
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%',
    border: 'none',
    outline: 'none',
    color: 'var(--text-primary)',
    resize: 'vertical',
    lineHeight: 1.6,
    fontSize: '12px',
    fontFamily: 'inherit',
  },
  detailText: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
    margin: 0,
    whiteSpace: 'pre-wrap',
  },
  addRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  addInput: {
    flex: 1,
    fontSize: '13px',
    color: 'var(--text-primary)',
    background: 'transparent',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    padding: '6px 10px',
    outline: 'none',
    fontFamily: 'inherit',
  },
  muted: { fontSize: '12px', color: 'var(--text-secondary)' },
}
